import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { usePantryStore } from '../../../providers/PantryProvider'
import type { WHLocation } from '../../../types/warehouse'
import { useLocationListQuery } from '../hooks/useLocationListQuery'

const DEFAULT_LIMIT = 10
const SEARCH_DEBOUNCE_MS = 300

interface LocationListStats {
  total: number
  posConnectedTotal: number
  visibleOnSiteTotal: number
}

interface LocationListContextValue {
  items: WHLocation[]
  page: number
  limit: number
  totalItems: number
  totalPages: number
  search: string
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  error: Error | null
  stats: LocationListStats
  setPage: (page: number) => void
  setLimit: (limit: number) => void
  setSearch: (value: string) => void
  refetch: () => void
}

const LocationListContext = createContext<LocationListContextValue | null>(null)

interface LocationListProviderProps {
  children: ReactNode
}

export const LocationListProvider = ({
  children,
}: LocationListProviderProps) => {
  const store = usePantryStore()
  const storeId = store?.storeId ?? ''
  const orgId = store?.orgId

  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(DEFAULT_LIMIT)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedSearch(search.trim())
    }, SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(id)
  }, [search])

  // Reset to page 1 whenever the search changes (after debounce).
  useEffect(() => {
    setPage(1)
  }, [debouncedSearch])

  const query = useLocationListQuery({
    storeId,
    organizationId: orgId,
    page,
    limit,
    search: debouncedSearch,
  })

  const items = query.data?.items ?? []

  const stats = useMemo<LocationListStats>(
    () => ({
      total: query.data?.totalItems ?? 0,
      posConnectedTotal: query.data?.totalPosConnected ?? 0,
      visibleOnSiteTotal: query.data?.totalVisible ?? 0,
    }),
    [
      query.data?.totalItems,
      query.data?.totalPosConnected,
      query.data?.totalVisible,
    ],
  )

  const handleSetLimit = (next: number) => {
    setLimit(next)
    setPage(1)
  }

  const value: LocationListContextValue = {
    items,
    page,
    limit,
    totalItems: query.data?.totalItems ?? 0,
    totalPages: query.data?.totalPages ?? 0,
    search,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: (query.error as Error | null) ?? null,
    stats,
    setPage,
    setLimit: handleSetLimit,
    setSearch,
    refetch: () => {
      query.refetch()
    },
  }

  return (
    <LocationListContext.Provider value={value}>
      {children}
    </LocationListContext.Provider>
  )
}

export const useLocationListContext = (): LocationListContextValue => {
  const ctx = useContext(LocationListContext)
  if (!ctx) {
    throw new Error(
      'useLocationListContext must be used within a LocationListProvider',
    )
  }
  return ctx
}
